import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Shield, FileText, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AffiliateTaxInfoPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnedFromStripe = searchParams.get('status');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/affiliate/stripe-status`, { withCredentials: true });
      setStatus(data);
      setAuthRequired(false);
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthRequired(true);
      } else {
        toast.error(err.response?.data?.detail || 'Unable to load status');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    if (returnedFromStripe === 'complete') {
      toast.success('Tax info submitted successfully — checking status…');
    }
  }, [returnedFromStripe]); // eslint-disable-line react-hooks/exhaustive-deps

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/affiliate/stripe-onboarding`, {}, { withCredentials: true });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Unable to start onboarding');
        setStarting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stripe onboarding unavailable. Please contact support.');
      setStarting(false);
    }
  };

  const allDone = status?.details_submitted && status?.payouts_enabled;

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-4" data-testid="tax-info-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/affiliate/dashboard" className="inline-flex items-center text-[#A1A7B5] hover:text-white text-sm mb-8" data-testid="back-to-dashboard-link">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Link>

        <div className="glass-card p-8 md:p-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <span className="text-[#00F0FF] text-xs font-bold uppercase tracking-[3px]">Secure · Powered by Stripe</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Payout &amp; Tax Information
          </h1>
          <p className="text-[#A1A7B5] text-base leading-relaxed mb-8 max-w-xl">
            Before we can send your first commission payout, we need to verify your identity and collect tax info (W-9 / W-8BEN). Stripe handles the entire flow securely and automatically files your 1099-NEC at year-end if you earn $600+.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-[#A1A7B5]" data-testid="tax-info-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading status…
            </div>
          ) : authRequired ? (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 mb-6" data-testid="tax-info-auth-required">
              <h3 className="text-lg font-semibold text-white mb-2">Please log in to continue</h3>
              <p className="text-[#A1A7B5] text-sm mb-4">
                You need to be signed in to your affiliate account before setting up your payout and tax info. Use the email and password you created when you applied.
              </p>
              <Button onClick={() => navigate('/affiliate?next=/affiliate/tax-info')} className="btn-primary" data-testid="login-cta-btn">
                Log in &rarr;
              </Button>
            </div>
          ) : allDone ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-6" data-testid="tax-info-complete">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <h3 className="text-lg font-semibold text-white">All set — payouts are enabled</h3>
              </div>
              <p className="text-[#A1A7B5] text-sm">Your account is verified and ready to receive commission payouts. Your 1099 will be issued automatically at year-end if you earn $600 or more.</p>
              <Button onClick={startOnboarding} disabled={starting} className="btn-secondary mt-4" data-testid="update-info-btn">
                {starting ? 'Redirecting…' : 'Update Payment/Tax Info'}
              </Button>
            </div>
          ) : status?.has_account && status?.details_submitted && !status?.payouts_enabled ? (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 mb-6" data-testid="tax-info-pending-review">
              <h3 className="text-lg font-semibold text-white mb-2">Under Stripe review</h3>
              <p className="text-[#A1A7B5] text-sm mb-4">You've submitted your info. Stripe is verifying your identity and bank. This usually takes a few minutes to 24 hours.</p>
              {status.requirements_currently_due?.length > 0 && (
                <div>
                  <p className="text-[#A1A7B5] text-xs mb-2">Stripe still needs:</p>
                  <ul className="text-[#A1A7B5] text-xs list-disc list-inside">
                    {status.requirements_currently_due.map((r) => <li key={r}>{r.replace(/\./g, ' · ')}</li>)}
                  </ul>
                  <Button onClick={startOnboarding} disabled={starting} className="btn-primary mt-4" data-testid="continue-onboarding-btn">
                    {starting ? 'Redirecting…' : 'Continue Onboarding'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 mb-6" data-testid="tax-info-not-started">
              <div className="bg-[#111621] border border-[#1E2532] rounded-xl p-5 flex items-start gap-4">
                <FileText className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-semibold mb-1">What you'll need</p>
                  <ul className="text-[#A1A7B5] text-sm space-y-1 list-disc list-inside">
                    <li>Legal name &amp; SSN or EIN (required for 1099 filing)</li>
                    <li>Date of birth &amp; residential address</li>
                    <li>Bank account for direct-deposit payouts</li>
                  </ul>
                </div>
              </div>
              <div className="bg-[#111621] border border-[#1E2532] rounded-xl p-5 flex items-start gap-4">
                <CreditCard className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-semibold mb-1">How payouts work</p>
                  <p className="text-[#A1A7B5] text-sm">Commissions accrue as you drive sales. Once your balance reaches $25, you can request a payout from your dashboard. Funds arrive in your bank within 2 business days via Stripe.</p>
                </div>
              </div>
              <Button
                onClick={startOnboarding}
                disabled={starting}
                className="btn-primary w-full py-4 text-sm font-bold tracking-wider uppercase"
                data-testid="start-onboarding-btn"
              >
                {starting ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirecting to Stripe…</>) : 'Start Secure Setup →'}
              </Button>
            </div>
          )}

          <p className="text-[#6B7280] text-xs leading-relaxed mt-8 pt-6 border-t border-[#1E2532]">
            RENEW LABS never sees or stores your full SSN, EIN, or bank account number — Stripe handles this directly. Stripe is PCI-DSS Level 1 certified and SOC-1/SOC-2 compliant. For privacy details, see <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline">Stripe's privacy policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateTaxInfoPage;
